// On-device photo quality, measured on a small grayscale copy of a frame
// (~160px wide → 1–2 ms). Pure functions over plain arrays so they run the
// same on a live camera frame, a captured photo, and in unit tests.
//
//   luma       mean brightness 0–255            → "too dark"
//   sharpness  variance of the 4-neighbour      → "blurry"
//              Laplacian (edge energy)
//   motion     mean abs difference between two  → "hold still"
//              40×40 thumbnails of consecutive frames
//   glare      share of an area blown out to    → "step to the side"
//              white (a reflection of the sun or sky on the paint)
//
// Thresholds live in QUALITY and are tuned with the camera debug overlay
// (?camdebug=1) on real phones.

export interface GrayFrame {
  data: Float32Array
  width: number
  height: number
}

export interface FrameMetrics {
  luma: number
  sharpness: number
}

export const QUALITY = {
  // Mean luma below this reads as "too dark" to a person, not just a sensor.
  DARK_LUMA: 50,
  // Absolute sharpness floor at ~160px — below this nothing is in focus.
  BLUR_FLOOR: 18,
  // Relative floor: a frame this much softer than the best one seen for the
  // same shot is blurry (handles low-texture subjects like a white door).
  BLUR_RELATIVE: 0.55,
  // Mean abs thumbnail difference (0–255) under which the phone is "still".
  STILL_MOTION: 7,
  METRICS_WIDTH: 160,
  MOTION_SIZE: 40,
  // A pixel this bright is blown out: nothing under it shows in the photo.
  GLARE_LUMA: 250,
  // This much of the area blown out reads as a reflection over the damage;
  // once seen, it counts as gone only below GLARE_CLEAR (no flicker).
  GLARE_SHARE: 0.1,
  GLARE_CLEAR: 0.07,
} as const

// Part of a frame, 0–1 of its width and height.
export interface Area {
  x: number
  y: number
  w: number
  h: number
}

export function toGray(rgba: ArrayLike<number>, width: number, height: number): GrayFrame {
  const data = new Float32Array(width * height)
  for (let i = 0, p = 0; p < data.length; i += 4, p++) {
    // Rec. 601 luma — what "bright" means to an eye.
    data[p] = 0.299 * rgba[i] + 0.587 * rgba[i + 1] + 0.114 * rgba[i + 2]
  }
  return { data, width, height }
}

export function meanLuma(frame: GrayFrame): number {
  let sum = 0
  for (let i = 0; i < frame.data.length; i++) sum += frame.data[i]
  return frame.data.length ? sum / frame.data.length : 0
}

export function laplacianVariance(frame: GrayFrame): number {
  const { data, width: w, height: h } = frame
  if (w < 3 || h < 3) return 0
  let sum = 0
  let sumSq = 0
  let n = 0
  for (let y = 1; y < h - 1; y++) {
    for (let x = 1; x < w - 1; x++) {
      const i = y * w + x
      const lap = data[i - w] + data[i + w] + data[i - 1] + data[i + 1] - 4 * data[i]
      sum += lap
      sumSq += lap * lap
      n++
    }
  }
  const mean = sum / n
  return sumSq / n - mean * mean
}

export function measure(frame: GrayFrame): FrameMetrics {
  return { luma: meanLuma(frame), sharpness: laplacianVariance(frame) }
}

// Nearest-neighbour downsample to a square thumbnail for motion checks.
export function thumbnail(frame: GrayFrame, size: number = QUALITY.MOTION_SIZE): Float32Array {
  const out = new Float32Array(size * size)
  for (let y = 0; y < size; y++) {
    const sy = Math.min(frame.height - 1, Math.floor(((y + 0.5) * frame.height) / size))
    for (let x = 0; x < size; x++) {
      const sx = Math.min(frame.width - 1, Math.floor(((x + 0.5) * frame.width) / size))
      out[y * size + x] = frame.data[sy * frame.width + sx]
    }
  }
  return out
}

export function motionBetween(a: Float32Array, b: Float32Array): number {
  if (a.length !== b.length || a.length === 0) return Infinity
  let sum = 0
  for (let i = 0; i < a.length; i++) sum += Math.abs(a[i] - b[i])
  return sum / a.length
}

// Share (0–1) of the area that's blown out to white.
export function glareShare(frame: GrayFrame, area: Area = { x: 0, y: 0, w: 1, h: 1 }): number {
  const { data, width: w, height: h } = frame
  const x0 = Math.max(0, Math.floor(area.x * w))
  const x1 = Math.min(w, Math.ceil((area.x + area.w) * w))
  const y0 = Math.max(0, Math.floor(area.y * h))
  const y1 = Math.min(h, Math.ceil((area.y + area.h) * h))
  let hot = 0
  let n = 0
  for (let y = y0; y < y1; y++) {
    for (let x = x0; x < x1; x++) {
      if (data[y * w + x] >= QUALITY.GLARE_LUMA) hot++
      n++
    }
  }
  return n ? hot / n : 0
}

export function glareIssue(share: number, wasGlaring = false): boolean {
  return share >= (wasGlaring ? QUALITY.GLARE_CLEAR : QUALITY.GLARE_SHARE)
}

export type LightIssue = "dark" | null
export type FocusIssue = "blurry" | null

export function lightIssue(m: FrameMetrics): LightIssue {
  return m.luma < QUALITY.DARK_LUMA ? "dark" : null
}

// `best` = the sharpest value seen for this shot so far (live camera); pass 0
// for a single photo to use the absolute floor only.
export function focusIssue(m: FrameMetrics, best = 0): FocusIssue {
  if (m.sharpness < QUALITY.BLUR_FLOOR) return "blurry"
  if (best > 0 && m.sharpness < best * QUALITY.BLUR_RELATIVE) return "blurry"
  return null
}
